import React from 'react';
import UseCasePageTemplate from '../../../components/UseCasePageTemplate';
import DataUserAnimation from '../../../components/DataUserAnimation';

const DataUserPage = () => {
  // User icon
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );

  // Icons for solutions
  const trustIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const discoveryIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const insightIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  const challenges = [
    {
      title: "Data Trust Issues",
      description: "Uncertainty about the reliability and accuracy of data, leading to lack of confidence in making data-driven decisions."
    },
    {
      title: "Data Discovery Challenges",
      description: "Difficulty finding the right data assets and understanding their context, lineage, and quality."
    },
    {
      title: "Siloed Knowledge",
      description: "Limited visibility into data preparation processes and quality checks performed before data reaches dashboards and reports."
    },
    {
      title: "Reactive Problem Reporting",
      description: "Having to manually report issues after discovering incorrect data, with slow resolution processes and limited communication."
    }
  ];

  const solutions = [
    {
      icon: trustIcon,
      title: "Data Trust and Transparency",
      description: "Build confidence in your data with clear quality metrics and certification of trustworthy data assets.",
      items: [
        "Data quality score cards for all assets",
        "Certification badges for verified data sources",
        "Transparent lineage showing data transformations",
        "Real-time quality status indicators",
        "Historical reliability metrics and trends"
      ]
    },
    {
      icon: discoveryIcon,
      title: "Self-Service Data Discovery",
      description: "Easily find and understand available data assets with comprehensive metadata and contextual information.",
      items: [
        "Searchable data catalog with quality metrics",
        "Business glossary integration",
        "Usage metrics showing popular and reliable assets",
        "Contextual documentation and descriptions",
        "Related asset recommendations"
      ]
    },
    {
      icon: insightIcon,
      title: "Collaborative Issue Resolution",
      description: "Streamline the process of reporting and resolving data quality issues with built-in collaboration tools.",
      items: [
        "One-click issue reporting from dashboards",
        "Automated routing to data owners",
        "Status tracking for reported issues",
        "Notification system for resolution updates",
        "Knowledge base of common issues and resolutions"
      ]
    }
  ];

  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Increased Decision Confidence",
      description: "Make business decisions with confidence knowing your data is reliable, accurate, and up-to-date."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: "Accelerated Insights",
      description: "Reduce time spent validating data and questioning results, allowing for faster insight generation and action."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: "Enhanced Collaboration",
      description: "Improve communication and collaboration between data users and data producers with shared visibility and quality expectations."
    }
  ];

  const testimonial = {
    quote: "As a business analyst, I used to spend 30% of my time just verifying if data was accurate before presenting it. With Qupid, I can see at a glance which reports are trustworthy and focus on generating insights rather than validating data.",
    author: "Maria Rodriguez",
    title: "Senior Business Analyst",
    company: "Global Retail Corp"
  };

  const relatedUsesCases = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      title: "For Data Leaders",
      description: "Learn how Qupid helps Data Leaders drive strategic value from their data assets.",
      link: "/solutions/by-persona/data-leader"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      title: "For Data Engineers",
      description: "Discover how Qupid helps Data Engineers build reliable data pipelines.",
      link: "/solutions/by-persona/data-engineer"
    }
  ];

  return (
    <UseCasePageTemplate
      title="For Data Users"
      description="Access trustworthy data with confidence, discover relevant data assets easily, and collaborate effectively with data producers."
      heroAnimation={<DataUserAnimation />}
      icon={icon}
      challenges={challenges}
      solutions={solutions}
      benefits={benefits}
      testimonial={testimonial}
      relatedUsesCases={relatedUsesCases}
    />
  );
};

export default DataUserPage; 